import { Field, useFormikContext } from "formik";
import React, { FC, ReactElement, useEffect } from "react";
import { DAILY_OPTION, MONTHLY_OPTION, PATTERN, RECURRENCE, YEARLY_OPTION } from "../../../constants/notification-key";
import { useLocale } from "../../../contexts/localeContext";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { MultiSelectOption } from "../../../models/MultiSelectOption";
import { getTimeZones } from "../../../services/timeZoneService";
import { requiredValidation, validateRequiredEndDate } from "../../../utilities/Validation";
import DateField from "../../date-field/date-field";
import Label from "../../label/label";
import PaginatedSearchableSelectField from "../../selection-field/paginated-searchable-select-field";
import TimeField from "../../timeField/timeField";
import Daily from "./pattern/daily";
import Monthly from "./pattern/monthly";
import Weekly from "./pattern/weekly";
import Yearly from "./pattern/yearly";
import { Translator } from "../../../contexts/translator";
import { getDaysOfTheWeek } from "../../../utilities/DateUtils";

const Schedule: FC = () => {
    const { translate } = useLocale();
    const { errors, setFieldValue, validateField, values, validateForm } = useFormikContext<any>();

    // If the End By option is chosen, set the initial end date to tomorrow
    useEffect(() => {
        if (values.recurrenceOption === RECURRENCE.END_BY && !values.endDate) {
            const today = new Date(Date.now());
            const tomorrow = new Date(today);
            tomorrow.setDate(tomorrow.getDate() + 1);
            setFieldValue("endDate", tomorrow);
        }
    }, [values.recurrenceOption])

    useEffect(() => {
        validateForm()
    }, [values])
    
    useEffect(() => {
        if(values.time === ""){
            setFieldValue("time",new Date(Date.now()))
        }

        if (values.endDate || values.startDate) {
            validateField("startDate");
            validateField("endDate");
        }
    }, [values.endDate, values.startDate]);

    const frequencyOptions: MultiSelectOption[] = [
        { value: PATTERN.DAILY, label: translate.byKey(TranslationKeys.daily) },
        { value: PATTERN.WEEKLY, label: translate.byKey(TranslationKeys.weekly) },
        { value: PATTERN.MONTHLY, label: translate.byKey(TranslationKeys.monthly) },
        { value: PATTERN.YEARLY, label: translate.byKey(TranslationKeys.yearly) },
    ];
    const rangeRecurrenceOptions: MultiSelectOption[] = [
        { value: RECURRENCE.END_BY, label: translate.byKey(TranslationKeys.endBy) },
        // { value: RECURRENCE.END_AFTER, label: translate.byKey(TranslationKeys.endAfter) }, // hidden on purpose until supported
        { value: RECURRENCE.NO_END_DATE, label: translate.byKey(TranslationKeys.noEndDate) },
    ];

    const onOccurancePatternChange = (option: MultiSelectOption) => {
        setFieldValue("pattern", option.value);

        if (option.value === PATTERN.DAILY && !!!values.dailyOption) {
            setFieldValue("dailyOption", DAILY_OPTION.EVERY);
            setFieldValue("occurrences", "1");
        }
        else if (option.value === PATTERN.WEEKLY) {
            setFieldValue("dailyOption","")
            if (!!!values.daysOfTheWeek) {
                setFieldValue("daysOfTheWeek", [getDaysOfTheWeek(translate)[1].value]);
                setFieldValue("occurrences", "1");
            }
            else if (typeof values.daysOfTheWeek === "string"){
                setFieldValue("daysOfTheWeek", [values.daysOfTheWeek]);
                setFieldValue("occurrences", "1");
            } 
        }
        else if (option.value === PATTERN.MONTHLY) {
            setFieldValue("monthlyOption", MONTHLY_OPTION.DAY);
        }
        else if (option.value === PATTERN.YEARLY) {  
            setFieldValue("yearlyOption", YEARLY_OPTION.ON);
        }
    }

    const onRangeRecurrenceChange = (option: MultiSelectOption) => {
        setFieldValue("recurrenceOption", option.value);
    }

    const getPatternToShow = (pattern: string): ReactElement => {
        switch (pattern) {
            case PATTERN.DAILY:
                return <Daily />
            case PATTERN.WEEKLY:
                return <Weekly />
            case PATTERN.MONTHLY:
                return <Monthly />
            case PATTERN.YEARLY:
                return <Yearly />
            default:
                return <>Not known</>
        }
    }

    const checkErrors = (values: any, errors: any, translate: Translator) => {
        /* daily errors */
        if (errors.occurrences && values.pattern === PATTERN.DAILY && values.dailyOption === DAILY_OPTION.EVERY) {
            return <div>
                <p className="text-xs text-red-500">
                    {errors.occurrences}
                </p>
            </div>
        }
        
        /* weekly errors */
        else if ((errors.occurrences || errors.daysOfTheWeek) && values.pattern === PATTERN.WEEKLY) {
            return <div>
                <p className="text-xs text-red-500">
                    {errors.occurrences}
                </p>
                <p className="text-xs text-red-500">
                    {errors.daysOfTheWeek}
                </p>
            </div>
        }
    
        /* monthly errors */
        else if ((errors.occurrences || errors.dayOfMonth) && values.pattern === PATTERN.MONTHLY && values.monthlyOption === MONTHLY_OPTION.DAY) {
            return <div>
                <p className="text-xs text-red-500">
                    {errors.occurrences}
                </p>
                <p className="text-xs text-red-500">
                    {errors.dayOfMonth}
                </p>
            </div>
        }
        else if ((errors.occurrences || !values.occurrence || !values.daysOfTheWeek) && values.pattern === PATTERN.MONTHLY && values.monthlyOption === MONTHLY_OPTION.THE) {
            return <div>
                {values.occurrence ? null :
                <p className="text-xs text-red-500">
                    {translate.byKey(TranslationKeys.pleaseSelectAnOccurrence)}
                </p>}
    
                {values.daysOfTheWeek ? null :
                <p className="text-xs text-red-500">
                    {translate.byKey(TranslationKeys.pleaseSelectADay)}
                </p>}
    
                <p className="text-xs text-red-500">
                    {errors.occurrences}
                </p>
            </div>
        }
                
        /* yearly options */
        else if ((errors.occurrences || !values.monthOfTheYear || errors.dayOfMonth) && values.pattern === PATTERN.YEARLY && values.yearlyOption === YEARLY_OPTION.ON) {
            return <div>
                <p className="text-xs text-red-500">
                    {errors.occurrences}
                </p>
                {values.monthOfTheYear ? null :
                <p className="text-xs text-red-500">
                    {translate.byKey(TranslationKeys.pleaseSelectAWeek)}
                </p>}
                <p className="text-xs text-red-500">
                    {errors.dayOfMonth}
                </p>
            </div>
        }  
        else if ((errors.occurrences || !values.occurrence || !values.daysOfTheWeek || !values.monthOfTheYear) && values.pattern === PATTERN.YEARLY && values.yearlyOption === YEARLY_OPTION.ON_THE) {
            return <div>
                <p className="text-xs text-red-500">
                    {errors.occurrences}
                </p>
                {values.occurrence ? null :
                <p className="text-xs text-red-500">
                    {translate.byKey(TranslationKeys.pleaseSelectAnOccurrence)}
                </p>}
                {values.daysOfTheWeek ? null :
                <p className="text-xs text-red-500">
                    {translate.byKey(TranslationKeys.pleaseSelectADay)}
                </p>}
                {values.monthOfTheYear ? null :
                <p className="text-xs text-red-500">
                    {translate.byKey(TranslationKeys.pleaseSelectAMonth)}
                </p>}
            </div>
        }  

        return null
    }

    return <>
        <h1 className="mb-3 text-portal-blue-primary-shade2 text-xl font-semibold">
            {translate.byKey(TranslationKeys.schedule)}
        </h1>

        <div className="flex flex-row ">
            {/*Time selection*/}
            <div className="flex flex-col">
                <h3 className="text-sm font-bold mb-3">
                    {translate.byKey(TranslationKeys.time)}:
                </h3>
                <div className="flex flex-col justify-between" id="time" >
                    <Field
                        name="time"
                        valueName="time"
                        placeholder={"--:--"}
                        component={TimeField}
                        validate={(value) => requiredValidation(value, translate)}
                        required={true}
                        width="w-64" />
                    {errors.time ? <span className='text-red-600 text-xs pr-6'>{errors.time}</span> : null}
                </div>
            </div>

            {/*Time zone*/}
            <div className="px-16">
                <h3 className="text-sm font-bold mb-3">
                    {translate.byKey(TranslationKeys.timeZone)}:
                </h3>
                <div className="flex flex-col justify-between w-64" id="time" >
                    <PaginatedSearchableSelectField
                        name={translate.byKey(TranslationKeys.timeZone)}
                        key="timeZoneSelectField"
                        accessor="timeZoneId"
                        dataMethod={getTimeZones}
                        searchAccessor={"zone"}
                        required={true}
                        onSelect={(_,item?) => {      
                            setFieldValue("timeZone", item?.label);
                            setFieldValue("timeZoneId", item?.value);
                        }}
                        selectedItemLabel={values.timeZone} />
                    {errors.timeZoneId ? <span className='text-red-600 text-xs pr-6'>{errors.timeZoneId}</span> : null}
                </div>
            </div>
        </div>

        {/*Recurrence Pattern*/}
        <div className="pt-5">
            <h3 className="text-sm font-bold">
                {translate.byKey(TranslationKeys.recurrencePattern)}
            </h3>
            <div className="flex flex-row">
                <div className="flex flex-col">
                    {frequencyOptions.map((option, index) => (
                        <div
                            key={option.value}
                            className="flex items-center text-sm font-medium ">
                            <Field
                                id={`radio-${option.value}`}
                                type="radio"
                                name="pattern"
                                value={option.value}
                                onChange={(_: any) => onOccurancePatternChange(option)} />
                            <Label htmlFor={`radio-${option.value}`} className="pl-1 text-sm">{option.label}</Label>
                        </div>
                    ))}
                </div>

                <div className="h-grow w-px bg-gray-400 mx-2 mt-2 ml-6"></div>

                <div className="flex h-grow items-center">
                    {getPatternToShow(values.pattern)}
                </div>
            </div>
        </div>

        {/* Range recurrence */}
        <div className="pt-5">
            <h3 className="text-sm font-bold">
                {translate.byKey(TranslationKeys.rangeOfrecurrence)}
            </h3>
            <div className="flex flex-row pt-2 space-x-2">
                <div className="flex flex-row space-x-2">
                    <Label className="text-sm" htmlFor={"startDate"}>{translate.byKey(TranslationKeys.start)}</Label>
                    <div className="flex flex-col">
                        <Field
                            valueName="startDate"
                            type="text"
                            name="startDate"
                            className="relative"
                            useOnMouseDown
                            component={DateField}
                            validate={(value) => requiredValidation(value, translate)}
                            width={"w-56"} 
                            required={true}
                        />
                        <div className="flex flex-col justify-between ">
                            {errors.startDate ? (<span className="text-red-600 text-xs">{errors.startDate}</span>) : null}
                        </div>
                    </div>
                </div>
                <div className="flex flex-col space-y-1">
                    {rangeRecurrenceOptions.map((option, index) => (
                        <div
                            key={option.value}
                            className="flex items-center text-sm font-medium ">
                            <Field
                                id={`radio-${option.value}`}
                                type="radio"
                                name="recurrenceOption"
                                value={option.value}
                                onChange={(_: any) => onRangeRecurrenceChange(option)}
                                validate={(value) => requiredValidation(value, translate)}
                                required={true}
                            />
                            <Label htmlFor={`radio-${option.value}`} className="pl-1 text-sm">{option.label}</Label>
                        </div>
                    ))}
                </div>
                <div className="flex flex-col">
                    {
                        values.recurrenceOption === RECURRENCE.END_BY ?
                            <>
                                <Field
                                    valueName="endDate"
                                    type="text"
                                    name="endDate"
                                    className="relative"
                                    useOnMouseDown
                                    component={DateField}
                                    validate={(value) => validateRequiredEndDate(value, values.startDate, translate)}
                                    width={"w-56"} 
                                    required={true}
                                />
                                    <div className="flex flex-row justify-between ">
                                    {errors.endDate ? (<span className="text-red-600 text-xs">{errors.endDate}</span>) : null}
                                </div>
                            </> 
                            : values.recurrenceOption === RECURRENCE.END_AFTER ?
                                <>
                                <div className="flex flex-row justify-between">
                                    <Field
                                        name="rangeOccurrence"
                                        valueName="rangeOccurrence"
                                        type="text"
                                        validate={(value) => requiredValidation(value, translate)}
                                        className={`p-1 text-sm w-16 h-6 bg-gray-input overflow-hidden`} 
                                        required={true}
                                    /> 
                                    <Label htmlFor={`radio-occurrence`} className="pl-3 text-sm">{translate.byKey(TranslationKeys.occurrences)}</Label>
                                </div>
                                    <div className="flex flex-row justify-between ">
                                        {errors.rangeOccurrence ? (<span className="text-red-600 text-xs">{errors.rangeOccurrence}</span>) : null}
                                    </div>
                                </>
                                : <></>
                    }
                </div>
            </div>
        </div>
        {checkErrors(values, errors, translate)}
    </>
}

export default Schedule;
